/**
 * Brinde da LP — modal no lugar do carrinho gaveta.
 *
 * Fluxo: o cliente adiciona um dos produtos da LP → em vez da sacola abre uma
 * modal com o brinde (galeria da cor selecionada, swatches e tamanhos) → ele
 * adiciona o brinde OU fecha a modal → aí sim a sacola abre.
 *
 * COMO O DRAWER É SEGURADO
 * Na 3.x bastava `window.__consoSilentCartUpdate`: o `conso-cart-drawer-guard.js`
 * embrulhava o `showDialog` do `cart-drawer-component` e a flag desligava a
 * abertura. Na 4.x quem abre a sacola é o `<theme-drawer>` — o cart-drawer faz
 * `this.closest('theme-drawer').open()` — e o `cart-drawer-component` nem herda
 * mais de `DialogComponent`. Ou seja: aquele guard virou no-op, e a flag sozinha
 * não segura mais nada.
 *
 * Aqui a guarda é local e com escopo estreito: embrulha `ThemeDrawer.open` e só
 * ignora a abertura do drawer QUE CONTÉM a sacola, e só enquanto esta modal está
 * no meio de abrir. Fora dessa janela nada muda — clique no ícone da sacola,
 * menu, qualquer outro drawer seguem normais. O arquivo global não é tocado.
 *
 * PREÇO
 * O brinde é grátis por CUPOM, não por function de desconto. A modal mostra o
 * preço cheio riscado e a palavra do setting no lugar do valor; a linha entra no
 * carrinho pelo preço normal e o abatimento só acontece quando o cupom entra.
 * Enquanto o cupom não for automático, use o setting `nota` da section pra dizer
 * isso ao cliente — a sacola vai mostrar o valor cheio.
 *
 * A linha carrega a property `_conso_brinde_lp`. Ela não muda preço nenhum
 * (nenhuma function lê essa chave): serve pra não reabrir a modal quando o
 * cliente adiciona um segundo produto e pra dar atribuição no pedido.
 */
(() => {
  if (window.__consoLpBrinde) return;
  window.__consoLpBrinde = true;

  const SEL_BLOCO = '.conso-lp-produto';
  // Fora da LP (numa PDP) não existe wrapper `.conso-lp-produto`. O que
  // identifica o produto do clique é o `data-product-id` que o Horizon põe no
  // formulário de compra e na barra sticky — os dois lugares de onde sai um add.
  const SEL_FORM = 'product-form-component[data-product-id], sticky-add-to-cart[data-product-id]';
  const SEL_ATC = 'button[type="submit"], .add-to-cart-button, .sticky-add-to-cart__button';
  const SEL_DADOS = 'script[type="application/json"][data-conso-lp-brinde]';
  const EVENTO_ADD = 'shopify:cart:lines-update';
  const PROP_BRINDE = '_conso_brinde_lp';
  // Marca do brinde progressivo, escrita pelo `conso-cart-gift-auto-add.js`.
  // Repetida aqui (e no snippet da barra) porque é o nome da property, não uma
  // constante compartilhável entre Liquid e JS.
  const PROP_PROGRESSIVO = '_conso_brinde_progressivo';
  const MARCA_GUARDA = '__consoLpBrindeGuarded';
  // Quantos gatilhos esta linha de brinde exigiu (só no modo alternativo).
  // Hoje nenhuma function lê: fica na linha pra atribuição e pra, se um dia a
  // function passar a dividir os gatilhos por isso, o dado já estar lá.
  const PROP_GATILHOS = '_conso_brinde_gatilhos';

  // Se o add falhar sem disparar evento nenhum, a guarda ficaria ligada e a
  // sacola não abriria mais na página. Este é o teto.
  const TETO_ESPERA_MS = 8000;

  const rotas = () => (window.Theme && window.Theme.routes) || {};

  let pendente = false;
  // Handle do produto do bloco que armou a interceptação — é ele que decide
  // QUAL brinde a modal vai oferecer quando o add terminar.
  let handlePendente = '';
  let produtoIdPendente = '';
  let secaoPendente = '';
  let expiracao = null;
  let segurando = false;
  let modal = null;
  let confirma = null;
  let meta = null;
  // Preenchido no add quando ainda falta dinheiro pro próximo degrau. É o que
  // faz o 'close' da modal desviar pra modal de meta em vez de abrir a sacola.
  let metaPendente = null;
  // Brinde do add de gatilho em andamento. Lido (e zerado) no `abrirSacola`,
  // que avisa a página que o fluxo terminou.
  let dadosDoFluxo = null;
  let aceitou = false;
  let recusou = false;
  let via = 'esc';

  /* ------------------------------------------------------- guarda do drawer */

  function armarGuarda() {
    const ThemeDrawer = window.customElements && customElements.get('theme-drawer');
    if (!ThemeDrawer || ThemeDrawer.prototype[MARCA_GUARDA]) return;
    ThemeDrawer.prototype[MARCA_GUARDA] = true;

    const original = ThemeDrawer.prototype.open;
    if (typeof original !== 'function') return;

    ThemeDrawer.prototype.open = function () {
      if (segurando && this.querySelector('cart-drawer-component')) return undefined;
      return original.apply(this, arguments);
    };
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('theme-drawer').then(armarGuarda);
  }

  function liberar() {
    segurando = false;
  }

  function desarmar() {
    pendente = false;
    clearTimeout(expiracao);
    liberar();
  }

  /* ------------------------------------------------------------------ dados */

  function lerTodosDados() {
    return Array.from(document.querySelectorAll(SEL_DADOS))
      .map((el) => {
        try {
          const d = JSON.parse(el.textContent);
          return d && Array.isArray(d.variantes) && d.variantes.length ? d : null;
        } catch (e) {
          console.error('[conso-lp-brinde] JSON inválido na section', e);
          return null;
        }
      })
      .filter(Boolean);
  }

  /**
   * Qual brinde vale para o bloco que o cliente acabou de usar.
   *
   * A LP passou a ter dois brindes (as camisas dão a polo, o costume dá a
   * camisa), então cada section de brinde declara em `gatilhos` os handles dos
   * produtos que ela atende. Sem `gatilhos` a section é o padrão da página —
   * é o que mantém a LP de um brinde só funcionando sem configurar nada.
   *
   * Específico ganha do padrão: com dois candidatos, vale o que nomeia o
   * produto.
   */
  function escolherDados(handle, produtoId, secaoId) {
    // `secoes` (opcional): a section de brinde só atende cliques dentro destas
    // sections da página. Na Área de membros a mesma camisa aparece na vista da
    // polo brinde e na vista de 15%; sem isto as duas abririam a modal da polo.
    const todos = lerTodosDados().filter(
      (d) => !Array.isArray(d.secoes) || d.secoes.length === 0 || (secaoId && d.secoes.includes(secaoId))
    );
    if (todos.length === 0) return null;

    const porHandle = (d) =>
      Array.isArray(d.gatilhos) && d.gatilhos.length > 0 && handle && d.gatilhos.includes(handle);
    // Mesmo critério por id: é o que uma PDP usa, onde a section resolve o
    // handle do gatilho pro id do produto (ou assume o produto da página).
    const porId = (d) =>
      Array.isArray(d.gatilhosId) &&
      d.gatilhosId.length > 0 &&
      produtoId &&
      d.gatilhosId.some((id) => String(id) === String(produtoId));

    const especifico = todos.find((d) => porHandle(d) || porId(d));
    if (especifico) return especifico;

    // A section padrão (sem gatilho) só vale dentro de um bloco da LP. Fora
    // dele — numa PDP, onde o kit e as recomendações também têm formulário de
    // compra — só abre modal quem foi nomeado.
    if (!handle) return null;
    return todos.find((d) => !Array.isArray(d.gatilhos) || d.gatilhos.length === 0) || null;
  }

  /** Id da section do tema onde o clique aconteceu. */
  function secaoDoClique(elemento) {
    const secao = elemento && elemento.closest ? elemento.closest('[id^="shopify-section-"]') : null;
    return (secao && secao.id) || '';
  }

  /** Handle do produto do bloco onde o clique aconteceu. */
  function handleDoBloco(elemento) {
    const bloco = elemento && elemento.closest ? elemento.closest(SEL_BLOCO) : null;
    return (bloco && bloco.getAttribute('data-conso-produto')) || '';
  }

  /** Id do produto do formulário (ou sticky) onde o clique aconteceu. */
  function produtoIdDoClique(elemento) {
    const form = elemento && elemento.closest ? elemento.closest(SEL_FORM) : null;
    return (form && form.getAttribute('data-product-id')) || '';
  }

  /**
   * Modo alternativo da LP (`?modo=2`, ver sections/conso-lp-modo.liquid).
   * Com ele, a modal do brinde só abre quando a sacola tem `modoMinimo`
   * unidades de gatilho — é o acordo "leve 2 camisas" de quem veio do site
   * já com uma camisa na sacola. Sem o atributo, mínimo é 1: a LP de sempre.
   */
  const modoAtivo = () => Boolean(document.documentElement.getAttribute('data-conso-modo'));

  const minimoDeGatilhos = (dados) => {
    if (!modoAtivo()) return 1;
    const n = Math.floor(Number(dados.modoMinimo));
    return Number.isFinite(n) && n > 1 ? n : 1;
  };

  /** Unidades de gatilho desta section na sacola. */
  function unidadesDeGatilho(carrinho, dados) {
    const ids = new Set((dados.gatilhosId || []).map(String));
    if (ids.size === 0 || !carrinho || !Array.isArray(carrinho.items)) return 0;

    return carrinho.items.reduce(
      (soma, i) => (i && ids.has(String(i.product_id)) ? soma + Number(i.quantity || 0) : soma),
      0
    );
  }

  const coresComEstoque = (dados) => {
    const disponiveis = new Set(dados.variantes.map((v) => v[1]));
    return dados.cores.filter((c) => disponiveis.has(c.nome));
  };

  const tamanhosDa = (dados, cor) => dados.variantes.filter((v) => v[1] === cor).map((v) => v[2]);

  const varianteDe = (dados, cor, tamanho) => {
    const achado = dados.variantes.find((v) => v[1] === cor && v[2] === tamanho);
    return achado ? achado[0] : null;
  };

  /* --------------------------------------------------------------- carrinho */

  const idsDeSecaoDoCarrinho = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

  /** A resposta de erro do /cart/add muda de forma conforme o pedido. */
  const mensagemDeErro = (dados) => {
    const errors = dados && dados.errors;

    if (typeof errors === 'string' && errors.trim()) return errors;

    if (errors && typeof errors === 'object') {
      const primeira = Object.values(errors)
        .flat()
        .find((m) => typeof m === 'string' && m.trim());
      if (primeira) return primeira;
    }

    return (dados && (dados.description || dados.message)) || 'Não foi possível adicionar ao carrinho.';
  };

  async function postCarrinho(itens) {
    const corpo = { items: itens };
    const secoes = idsDeSecaoDoCarrinho();
    if (secoes.length) {
      corpo.sections = secoes.join(',');
      corpo.sections_url = window.location.pathname;
    }

    const resposta = await fetch(rotas().cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(corpo),
    });
    const dados = await resposta.json();
    if (!resposta.ok || dados.status) throw new Error(mensagemDeErro(dados));
    return dados;
  }

  async function carrinhoAtual() {
    const r = await fetch(`${rotas().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  /**
   * Aplica o cupom na sessão do carrinho.
   *
   * `/discount/<codigo>` é rota da própria Shopify: ela guarda o código no
   * carrinho e ele passa a valer já no `/cart.js` e na sacola, não só no
   * checkout. O `redirect` existe só pra o 302 cair num JSON curto em vez de
   * arrastar a home inteira.
   *
   * Falhar aqui não pode derrubar a adição do brinde: o cupom é conveniência,
   * e o bloco de copiar o código continua na página como plano B.
   */
  async function aplicarCupom(codigo) {
    try {
      await fetch(`/discount/${encodeURIComponent(codigo)}?redirect=/cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      return true;
    } catch (e) {
      console.warn('[conso-lp-brinde] não deu pra aplicar o cupom', e);
      return false;
    }
  }

  /**
   * Re-renderiza a sacola DEPOIS do cupom.
   *
   * As sections que voltam do `/cart/add` foram montadas antes de o cupom
   * existir — usá-las mostraria a polo por R$ 239,90 na gaveta. Um
   * `/cart/update.js` sem alteração nenhuma, só pedindo as sections, devolve o
   * carrinho já com o desconto.
   */
  async function renderizarCarrinho() {
    const corpo = {};
    const secoes = idsDeSecaoDoCarrinho();
    if (secoes.length) {
      corpo.sections = secoes.join(',');
      corpo.sections_url = window.location.pathname;
    }

    const r = await fetch(rotas().cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(corpo),
    });
    return r.json();
  }

  /**
   * O cliente já pegou ESTE brinde?
   *
   * Compara o produto, não só a marca: com dois brindes na mesma página, olhar
   * só a property faria a segunda oferta ser engolida pela primeira — quem
   * pegou a polo nunca veria a camisa.
   */
  const jaTemOBrinde = (carrinho, dados) =>
    Boolean(
      carrinho &&
        Array.isArray(carrinho.items) &&
        carrinho.items.some(
          (i) =>
            i &&
            i.properties &&
            i.properties[PROP_BRINDE] &&
            (!dados || String(i.product_id) === String(dados.produtoId))
        )
    );

  /**
   * Quantas unidades deste brinde já estão na sacola (soma por quantidade, não
   * por linha — o mesmo brinde escolhido de novo na mesma cor/tamanho funde na
   * linha existente). Usado só no modo "repetir por unidade" (`dados.porUnidade`):
   * promoções tipo "a cada costume, mais uma camisa", sem teto.
   */
  const quantidadeDoBrinde = (carrinho, dados) =>
    !carrinho || !Array.isArray(carrinho.items)
      ? 0
      : carrinho.items.reduce(
          (soma, i) =>
            i && i.properties && i.properties[PROP_BRINDE] && String(i.product_id) === String(dados.produtoId)
              ? soma + Number(i.quantity || 0)
              : soma,
          0
        );

  /**
   * Re-renderiza a sacola. Dispara o evento legado de propósito: quem converte
   * pro formato da 4.x é o `conso-cart-event-bridge.js`, e ele usa
   * `action: 'update'` — que é justamente o que impede o auto-open.
   */
  function avisarTema(carrinho, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: carrinho,
          sourceId: 'conso-lp-brinde',
          data: {
            itemCount: Number((carrinho && carrinho.item_count) || 0),
            source: 'conso-lp-brinde',
            sections,
          },
        },
      })
    );
  }

  /**
   * Fim do fluxo do brinde: a sacola vai abrir.
   *
   * Antes, avisa a página com o evento cancelável `conso:lp-brinde:fim`. Outra
   * oferta pode assumir o fim do fluxo (o upsell da Área de membros,
   * `assets/conso-area-upsell.js`): ela chama `preventDefault()` e abre a
   * sacola por conta própria com `detail.abrirSacola`. Sem ouvinte (a LP
   * pública), nada muda. Só dispara uma vez por add de gatilho.
   */
  function abrirSacola() {
    liberar();

    const fluxo = dadosDoFluxo;
    dadosDoFluxo = null;
    if (fluxo) {
      const fim = new CustomEvent('conso:lp-brinde:fim', {
        cancelable: true,
        detail: { brindeId: String(fluxo.produtoId), abrirSacola: abrirSacolaAgora },
      });
      if (!document.dispatchEvent(fim)) return;
    }

    abrirSacolaAgora();
  }

  function abrirSacolaAgora() {
    liberar();

    const drawer = document.querySelector('cart-drawer-component');
    const theme = drawer && drawer.closest('theme-drawer');
    if (theme && typeof theme.open === 'function') {
      theme.open();
      return;
    }
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    const gatilho = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (gatilho) gatilho.click();
  }

  /* --------------------------------------------------------------- tracking */

  // Nome próprio de propósito: `add_to_cart` já sai do pixel nativo nesta mesma
  // adição, e os dois juntos dobram o valor da conversão no Ads. O prefixo
  // `brinde_` segue a regra dos `kit_*` — nome padrão do GA4 registrado como
  // definição personalizada fica ambíguo pra sempre.
  function rastrear(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  /* ------------------------------------------------------------------ modal */

  function montarModal(dados) {
    if (modal) return modal;

    modal = document.createElement('dialog');
    modal.className = 'conso-brinde';
    modal.setAttribute('aria-labelledby', 'conso-brinde-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-brinde__caixa';

    const cabecalho = document.createElement('header');
    cabecalho.className = 'conso-brinde__cabecalho';

    const titulo = document.createElement('h2');
    titulo.className = 'conso-brinde__titulo';
    titulo.id = 'conso-brinde-titulo';
    titulo.textContent = dados.modalTitulo;

    cabecalho.appendChild(titulo);

    if (dados.modalSub) {
      const sub = document.createElement('p');
      sub.className = 'conso-brinde__sub';
      sub.textContent = dados.modalSub;
      cabecalho.appendChild(sub);
    }

    const fechar = document.createElement('button');
    fechar.type = 'button';
    fechar.className = 'conso-brinde__fechar';
    fechar.setAttribute('aria-label', 'Fechar e ir para a sacola');
    fechar.innerHTML =
      '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
    fechar.addEventListener('click', () => pedirConfirmacao(dados, 'x'));
    cabecalho.appendChild(fechar);

    const corpo = document.createElement('div');
    corpo.className = 'conso-brinde__corpo';

    caixa.append(cabecalho, corpo);
    modal.appendChild(caixa);

    // O <dialog> ocupa a tela toda e a caixa é filha: clique fora dela é no
    // backdrop. Não fecha direto — pergunta antes.
    modal.addEventListener('click', (e) => {
      if (e.target !== modal) return;
      pedirConfirmacao(dados, 'backdrop');
    });

    // ESC: o <dialog> fecharia sozinho. `cancel` é o único gancho que dá pra
    // segurar isso — sem o preventDefault a modal sumiria sem perguntar nada.
    modal.addEventListener('cancel', (e) => {
      e.preventDefault();
      pedirConfirmacao(dados, 'esc');
    });

    // A modal só fecha de verdade em dois caminhos: brinde adicionado ou recusa
    // confirmada. Os dois levam à sacola.
    modal.addEventListener('close', () => {
      if (!aceitou) rastrear({ event: 'brinde_skip', brinde_via: via });

      // Quem recusou o brinde vai direto pra sacola: puxar uma segunda modal
      // em cima de quem acabou de dizer não seria insistência, não oferta.
      if (aceitou && metaPendente) {
        const info = metaPendente;
        metaPendente = null;
        // Um tick depois: o <dialog> de cima ainda está soltando o scroll-lock,
        // e abrir o próximo no mesmo frame deixa o body travado.
        requestAnimationFrame(() => mostrarMeta(dados, info));
        return;
      }

      abrirSacola();
    });

    // Gaveta de medidas/provador: o `conso-kit-medidas.js` acha ela subindo do
    // botão até `[data-kmm-escopo]`. A gaveta nasce escondida no corpo normal
    // da página (`sections/conso-lp-brinde.liquid`) e muda de dono aqui, uma
    // vez só — <dialog> aninhado funciona normal, o showModal() da gaveta vai
    // pro top layer independente de onde ela mora no DOM.
    if (dados.medidasId) {
      const origem = document.querySelector('.conso-lp-brinde-medidas-origem dialog.kmm');
      if (origem) {
        modal.setAttribute('data-kmm-escopo', '');
        caixa.appendChild(origem);
      }
    }

    document.body.appendChild(modal);
    return modal;
  }

  /**
   * Confirmação antes de desistir do brinde.
   *
   * Um segundo <dialog> em cima do primeiro, e não uma troca de conteúdo: assim
   * a escolha de cor e tamanho que o cliente já fez continua intacta atrás, e
   * "voltar para o brinde" é só fechar esta.
   */
  function montarConfirmacao(dados) {
    if (confirma) return confirma;

    confirma = document.createElement('dialog');
    confirma.className = 'conso-brinde-confirma';
    confirma.setAttribute('aria-labelledby', 'conso-brinde-confirma-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-brinde-confirma__caixa';

    const titulo = document.createElement('h2');
    titulo.className = 'conso-brinde-confirma__titulo';
    titulo.id = 'conso-brinde-confirma-titulo';
    titulo.textContent = dados.confirmaTitulo;
    caixa.appendChild(titulo);

    if (dados.confirmaTexto) {
      const texto = document.createElement('p');
      texto.className = 'conso-brinde-confirma__texto';
      texto.textContent = dados.confirmaTexto;
      caixa.appendChild(texto);
    }

    const acoes = document.createElement('div');
    acoes.className = 'conso-brinde-confirma__acoes';

    const voltar = document.createElement('button');
    voltar.type = 'button';
    voltar.className = 'conso-brinde-confirma__voltar button';
    voltar.textContent = dados.confirmaVoltar;
    voltar.addEventListener('click', () => confirma.close());

    const recusar = document.createElement('button');
    recusar.type = 'button';
    recusar.className = 'conso-brinde-confirma__recusar';
    recusar.textContent = dados.confirmaRecusar;
    recusar.addEventListener('click', () => {
      recusou = true;
      confirma.close();
      // O 'close' da modal do brinde é quem abre a sacola.
      if (modal) modal.close();
    });

    // "Voltar" primeiro no DOM: é a ação sugerida, e no mobile as duas
    // empilham na ordem em que aparecem.
    acoes.append(voltar, recusar);
    caixa.appendChild(acoes);
    confirma.appendChild(caixa);

    // Backdrop e ESC nesta aqui equivalem a "voltar": desistir do brinde é uma
    // decisão que precisa de clique deliberado.
    confirma.addEventListener('click', (e) => {
      if (e.target === confirma) confirma.close();
    });

    confirma.addEventListener('close', () => {
      if (!recusou) rastrear({ event: 'brinde_voltou', brinde_via: via });
    });

    document.body.appendChild(confirma);
    return confirma;
  }

  /* ------------------------------------------------------------ meta */

  /**
   * Quanto do carrinho conta para o brinde progressivo.
   *
   * Linha marcada como brinde (a polo desta LP ou o brinde progressivo já
   * ganho) não conta: ela é prêmio, não compra. Sem isso a polo de R$ 269,90
   * empurraria o cliente pro degrau sozinha, e o "falta X" mentiria — mesma
   * conta que o `conso-cart-gift-progress-bar` faz no Liquid.
   */
  function totalQualificado(carrinho) {
    if (!carrinho || !Array.isArray(carrinho.items)) return 0;

    const brindes = carrinho.items.reduce((soma, item) => {
      const props = (item && item.properties) || {};
      const ehBrinde = Boolean(props[PROP_BRINDE]) || Boolean(props[PROP_PROGRESSIVO]);
      return ehBrinde ? soma + Number(item.final_line_price || 0) : soma;
    }, 0);

    return Math.max(0, Number(carrinho.total_price || 0) - brindes);
  }

  /** O degrau mais barato que o carrinho ainda não alcançou. `null` = passou de todos. */
  function proximaMeta(totalCents, metas) {
    if (!Array.isArray(metas) || metas.length === 0) return null;

    return metas
      .filter((m) => m && Number(m.cents) > totalCents)
      .sort((a, b) => Number(a.cents) - Number(b.cents))[0] || null;
  }

  function emReais(cents) {
    const moeda = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL';

    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda }).format(cents / 100);
    } catch (e) {
      return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  /**
   * Modal de "falta X para o próximo brinde".
   *
   * Terceiro <dialog> do módulo, pelo mesmo motivo do de confirmação: empilhar
   * é mais simples do que trocar o conteúdo do primeiro, e cada um fecha no seu
   * próprio tempo. Este abre DEPOIS que a modal do brinde fecha, então os dois
   * nunca estão na tela ao mesmo tempo.
   */
  function montarMeta(dados) {
    if (meta) return meta;

    meta = document.createElement('dialog');
    meta.className = 'conso-brinde-meta';
    meta.setAttribute('aria-labelledby', 'conso-brinde-meta-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-brinde-meta__caixa';

    const fechar = document.createElement('button');
    fechar.type = 'button';
    fechar.className = 'conso-brinde-meta__x';
    fechar.setAttribute('aria-label', 'Fechar');
    fechar.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>';
    fechar.addEventListener('click', () => meta.close());
    caixa.appendChild(fechar);

    const titulo = document.createElement('h2');
    titulo.className = 'conso-brinde-meta__titulo';
    titulo.id = 'conso-brinde-meta-titulo';
    caixa.appendChild(titulo);

    const texto = document.createElement('p');
    texto.className = 'conso-brinde-meta__texto';
    texto.hidden = true;
    caixa.appendChild(texto);

    /* barra */
    const barra = document.createElement('div');
    barra.className = 'conso-brinde-meta__barra';
    barra.setAttribute('role', 'progressbar');
    barra.setAttribute('aria-valuemin', '0');
    barra.setAttribute('aria-valuemax', '100');

    const preenche = document.createElement('span');
    preenche.className = 'conso-brinde-meta__barra-fill';
    barra.appendChild(preenche);
    caixa.appendChild(barra);

    const escala = document.createElement('div');
    escala.className = 'conso-brinde-meta__escala';
    const escalaDe = document.createElement('span');
    const escalaAte = document.createElement('span');
    escala.append(escalaDe, escalaAte);
    caixa.appendChild(escala);

    /* ações */
    const acoes = document.createElement('div');
    acoes.className = 'conso-brinde-meta__acoes';

    const ir = document.createElement('a');
    ir.className = 'conso-brinde-meta__ir button';
    ir.textContent = dados.metaBotao || 'Ver produtos';
    ir.href = dados.metaUrl || '/';

    const seguir = document.createElement('button');
    seguir.type = 'button';
    seguir.className = 'conso-brinde-meta__seguir';
    seguir.textContent = dados.metaFechar || 'Continuar para o carrinho';
    seguir.addEventListener('click', () => meta.close());

    acoes.append(ir, seguir);
    caixa.appendChild(acoes);
    meta.appendChild(caixa);

    meta.addEventListener('click', (e) => {
      if (e.target === meta) meta.close();
    });

    // Único caminho que NÃO abre a sacola é o clique no botão: ali o cliente
    // está indo comprar mais, e empurrar a sacola por cima da navegação só
    // atrapalharia.
    let indoComprar = false;
    ir.addEventListener('click', () => {
      indoComprar = true;
      rastrear({ event: 'brinde_meta_click', brinde_meta_destino: ir.href });
    });

    meta.addEventListener('close', () => {
      if (!indoComprar) abrirSacola();
      indoComprar = false;
    });

    meta.__pintar = (info) => {
      titulo.textContent = String(dados.metaTitulo || 'Faltam {valor} para o próximo brinde').replace(
        '{valor}',
        emReais(info.faltamCents)
      );

      texto.textContent = dados.metaTexto || '';
      texto.hidden = !dados.metaTexto;

      const pct = Math.max(0, Math.min(100, (info.totalCents / info.metaCents) * 100));
      preenche.style.width = `${pct.toFixed(2)}%`;
      barra.setAttribute('aria-valuenow', String(Math.round(pct)));
      barra.setAttribute('aria-label', `Faltam ${emReais(info.faltamCents)} para o brinde`);

      escalaDe.textContent = emReais(info.totalCents);
      escalaAte.textContent = emReais(info.metaCents);

      ir.href = dados.metaUrl || '/';
    };

    document.body.appendChild(meta);
    return meta;
  }

  function mostrarMeta(dados, info) {
    const dlg = montarMeta(dados);
    dlg.__pintar(info);

    rastrear({
      event: 'brinde_meta_view',
      currency: dados.moeda,
      brinde_meta_falta: info.faltamCents / 100,
      brinde_meta_valor: info.metaCents / 100,
    });

    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
  }

  function pedirConfirmacao(dados, origem) {
    via = origem;
    recusou = false;
    const el = montarConfirmacao(dados);
    if (el.open) return;
    el.showModal();
  }

  function montarCard(dados) {
    const cores = coresComEstoque(dados);
    if (!cores.length) return null;

    const alvo = String(dados.corPadrao || '')
      .trim()
      .toLowerCase();
    const corInicial = cores.find((c) => c.nome.toLowerCase() === alvo) || cores[0];

    const estado = { cor: corInicial.nome, tamanho: '', foto: 0 };

    const card = document.createElement('article');
    card.className = 'conso-brinde-card';

    /* galeria */
    const galeria = document.createElement('div');
    galeria.className = 'conso-brinde-card__galeria';

    const palco = document.createElement('div');
    palco.className = 'conso-brinde-card__palco';
    const foto = document.createElement('img');
    foto.loading = 'lazy';
    foto.decoding = 'async';
    foto.alt = dados.titulo;
    palco.appendChild(foto);

    const miniaturas = document.createElement('div');
    miniaturas.className = 'conso-brinde-card__miniaturas';
    miniaturas.setAttribute('role', 'group');
    miniaturas.setAttribute('aria-label', 'Fotos do produto');

    galeria.append(palco, miniaturas);

    /* corpo */
    const dados_ = document.createElement('div');
    dados_.className = 'conso-brinde-card__corpo';

    if (dados.rotulo) {
      const rotulo = document.createElement('p');
      rotulo.className = 'conso-brinde-card__rotulo';
      rotulo.textContent = dados.rotulo;
      dados_.appendChild(rotulo);
    }

    const nome = document.createElement('h3');
    nome.className = 'conso-brinde-card__titulo';
    nome.textContent = dados.titulo;
    dados_.appendChild(nome);

    const precos = document.createElement('p');
    precos.className = 'conso-brinde-card__precos';
    const de = document.createElement('s');
    de.textContent = dados.de;
    const por = document.createElement('strong');
    por.textContent = dados.gratis;
    precos.append(de, por);
    dados_.appendChild(precos);

    if (dados.nota) {
      const nota = document.createElement('p');
      nota.className = 'conso-brinde-card__nota';
      nota.textContent = dados.nota;
      dados_.appendChild(nota);
    }

    /* cores */
    const blocoCor = document.createElement('div');
    blocoCor.className = 'conso-brinde-card__opcao';
    const legendaCor = document.createElement('p');
    legendaCor.className = 'conso-brinde-card__legenda';
    const valorCor = document.createElement('span');
    legendaCor.append(`${dados.rotuloCor}: `, valorCor);
    const faixaCor = document.createElement('div');
    faixaCor.className = 'conso-brinde-card__swatches';
    faixaCor.setAttribute('role', 'group');
    faixaCor.setAttribute('aria-label', dados.rotuloCor);
    blocoCor.append(legendaCor, faixaCor);
    dados_.appendChild(blocoCor);

    /* tamanhos */
    const blocoTam = document.createElement('div');
    blocoTam.className = 'conso-brinde-card__opcao';
    const legendaTam = document.createElement('p');
    legendaTam.className = 'conso-brinde-card__legenda';
    legendaTam.textContent = dados.rotuloTamanho;
    const faixaTam = document.createElement('div');
    faixaTam.className = 'conso-brinde-card__pills';
    faixaTam.setAttribute('role', 'group');
    faixaTam.setAttribute('aria-label', dados.rotuloTamanho);
    blocoTam.append(legendaTam, faixaTam);
    dados_.appendChild(blocoTam);

    /* medidas + provador virtual: dois links lado a lado, cada um abre a
       gaveta já na aba certa (gaveta movida pra dentro da modal em montarModal) */
    if (dados.medidasId) {
      const faixaMedidas = document.createElement('div');
      faixaMedidas.className = 'conso-brinde-card__medidas-faixa';

      const medidas = document.createElement('button');
      medidas.type = 'button';
      medidas.className = 'conso-brinde-card__medidas';
      medidas.setAttribute('data-km-medidas', dados.medidasId);
      medidas.setAttribute('data-km-medidas-titulo', dados.titulo);
      medidas.setAttribute('data-km-medidas-provador', '');
      medidas.setAttribute('data-km-medidas-sem-abas', '');
      medidas.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h18v8H3z"/><path d="M7 8v3M11 8v4M15 8v3M19 8v4"/></svg>';
      medidas.appendChild(document.createTextNode('Tabela de medidas'));

      const provador = document.createElement('button');
      provador.type = 'button';
      provador.className = 'conso-brinde-card__medidas';
      provador.setAttribute('data-km-medidas', dados.medidasId);
      provador.setAttribute('data-km-medidas-titulo', dados.titulo);
      provador.setAttribute('data-km-medidas-provador', '');
      provador.setAttribute('data-km-medidas-aba', 'provador');
      provador.setAttribute('data-km-medidas-sem-abas', '');
      provador.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 21V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14"/><path d="M9 21V11h6v10"/></svg>';
      provador.appendChild(document.createTextNode('Provador Virtual'));

      faixaMedidas.append(medidas, provador);
      dados_.appendChild(faixaMedidas);
    }

    /* ação */
    // Mesmas classes e mesmo markup interno do add-to-cart real (ver
    // `snippets/conso-add-to-cart-button.liquid`): a altura, o gap e o
    // alinhamento do ícone vêm de `.add-to-cart-button`, então o CTA do brinde
    // fica idêntico ao das camisas em vez de ser reconstruído por aproximação.
    const botao = document.createElement('button');
    botao.type = 'button';
    botao.className = 'button add-to-cart-button conso-brinde-card__botao';

    const botaoTexto = document.createElement('span');
    botaoTexto.className = 'add-to-cart-text';

    if (dados.iconeSacola) {
      const icone = document.createElement('span');
      icone.className = 'svg-wrapper add-to-cart-icon';
      icone.setAttribute('aria-hidden', 'true');
      // innerHTML porque é SVG, e a fonte é um asset do próprio tema mandado
      // pelo payload da section — não entra nada de fora aqui.
      icone.innerHTML = dados.iconeSacola;
      botaoTexto.appendChild(icone);
    }

    const botaoRotulo = document.createElement('span');
    botaoRotulo.className = 'add-to-cart-text__content';
    botaoRotulo.textContent = dados.textoBotao;
    botaoTexto.appendChild(botaoRotulo);

    botao.appendChild(botaoTexto);
    dados_.appendChild(botao);

    const erro = document.createElement('p');
    erro.className = 'conso-brinde-card__erro';
    erro.hidden = true;
    dados_.appendChild(erro);

    card.append(galeria, dados_);

    /* -------- render reativo -------- */

    const fotosDa = (nomeCor) => {
      const cor = cores.find((c) => c.nome === nomeCor);
      return (cor && Array.isArray(cor.imgs) ? cor.imgs : []).filter(Boolean);
    };

    function desenharGaleria() {
      const fotos = fotosDa(estado.cor);
      if (estado.foto >= fotos.length) estado.foto = 0;

      palco.hidden = fotos.length === 0;
      if (fotos.length) foto.src = fotos[estado.foto];

      // Uma foto só não é galeria: some com a faixa em vez de mostrar uma
      // miniatura solitária que não leva a lugar nenhum.
      miniaturas.textContent = '';
      miniaturas.hidden = fotos.length < 2;
      if (fotos.length < 2) return;

      fotos.forEach((src, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'conso-brinde-card__miniatura';
        b.setAttribute('aria-label', `Foto ${i + 1} de ${fotos.length}`);
        b.setAttribute('aria-pressed', String(i === estado.foto));
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = '';
        img.src = src;
        b.appendChild(img);
        b.addEventListener('click', () => {
          estado.foto = i;
          desenharGaleria();
        });
        miniaturas.appendChild(b);
      });
    }

    function desenharCores() {
      faixaCor.textContent = '';
      cores.forEach((cor) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'conso-brinde-card__swatch';
        b.title = cor.nome;
        b.setAttribute('aria-label', cor.nome);
        b.setAttribute('aria-pressed', String(cor.nome === estado.cor));
        if (cor.swatch) b.style.setProperty('--conso-swatch', cor.swatch);
        else b.classList.add('conso-brinde-card__swatch--sem');
        b.addEventListener('click', () => {
          if (estado.cor === cor.nome) return;
          estado.cor = cor.nome;
          estado.tamanho = '';
          estado.foto = 0;
          erro.hidden = true;
          desenhar();
        });
        faixaCor.appendChild(b);
      });
      valorCor.textContent = estado.cor;
    }

    function desenharTamanhos() {
      const tamanhos = tamanhosDa(dados, estado.cor);

      faixaTam.textContent = '';
      // Produto sem opção de tamanho: não desenha um seletor de nada.
      blocoTam.hidden = tamanhos.every((t) => !t);

      tamanhos.forEach((tamanho) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'conso-brinde-card__pill';
        b.textContent = tamanho;
        b.setAttribute('aria-pressed', String(tamanho === estado.tamanho));
        b.addEventListener('click', () => {
          estado.tamanho = tamanho;
          erro.hidden = true;
          desenhar();
        });
        faixaTam.appendChild(b);
      });
    }

    function desenhar() {
      desenharCores();
      desenharTamanhos();
      desenharGaleria();
    }

    desenhar();

    botao.addEventListener('click', async () => {
      const id = varianteDe(dados, estado.cor, estado.tamanho);

      if (!id) {
        erro.textContent = `Escolha o ${String(dados.rotuloTamanho).toLowerCase()}.`;
        erro.hidden = false;
        return;
      }

      botao.disabled = true;
      botao.classList.add('atc-loading');
      erro.hidden = true;

      try {
        const resposta = await postCarrinho([
          {
            id: Number(id),
            quantity: 1,
            properties: Object.assign(
              { [PROP_BRINDE]: dados.titulo },
              minimoDeGatilhos(dados) > 1 ? { [PROP_GATILHOS]: String(minimoDeGatilhos(dados)) } : {}
            ),
          },
        ]);

        let sections = resposta.sections;
        let carrinho = null;

        if (dados.aplicarCupom && dados.cupom) {
          await aplicarCupom(dados.cupom);
          try {
            const atualizado = await renderizarCarrinho();
            if (atualizado && atualizado.items) {
              carrinho = atualizado;
              if (atualizado.sections) sections = atualizado.sections;
            }
          } catch (e) {
            console.warn('[conso-lp-brinde] re-render pós-cupom falhou', e);
          }
        }

        if (!carrinho) carrinho = await carrinhoAtual();

        aceitou = true;

        rastrear({
          event: 'brinde_added',
          currency: dados.moeda,
          brinde_produto_id: String(dados.produtoId),
          brinde_produto: dados.titulo,
          brinde_variante: `${estado.cor} / ${estado.tamanho}`,
          brinde_valor_cheio: dados.valor,
          brinde_cupom: dados.aplicarCupom ? dados.cupom : null,
        });

        avisarTema(carrinho, sections);

        // Falta dinheiro pro próximo degrau? O 'close' abaixo desvia pra modal
        // de meta em vez de abrir a sacola. Já passou de todos os degraus (ou
        // não há degrau configurado) = segue direto pra sacola, como antes.
        metaPendente = null;
        if (dados.metaAtiva) {
          const totalCents = totalQualificado(carrinho);
          const alvo = proximaMeta(totalCents, dados.metas);

          if (alvo) {
            metaPendente = {
              totalCents,
              metaCents: Number(alvo.cents),
              faltamCents: Number(alvo.cents) - totalCents,
              brindes: alvo.brindes || [],
            };
          }
        }

        // Fechar dispara o handler de 'close', que é quem abre a sacola.
        modal.close();
      } catch (e) {
        console.error('[conso-lp-brinde]', e);
        erro.textContent = e && e.message ? e.message : 'Não foi possível adicionar.';
        erro.hidden = false;
      } finally {
        botao.disabled = false;
        botao.classList.remove('atc-loading');
      }
    });

    return card;
  }

  function abrirModal(dados) {
    const el = montarModal(dados);
    const corpo = el.querySelector('.conso-brinde__corpo');
    corpo.textContent = '';

    const card = montarCard(dados);

    // Brinde esgotado em todas as cores: não vale prender o cliente numa modal
    // vazia.
    if (!card) {
      abrirSacola();
      return;
    }

    corpo.appendChild(card);

    // Zerado a cada abertura: a modal é reaproveitada entre adições.
    aceitou = false;
    recusou = false;
    via = 'esc';

    el.showModal();

    rastrear({
      event: 'brinde_view',
      brinde_produto_id: String(dados.produtoId),
      brinde_produto: dados.titulo,
      brinde_modo: document.documentElement.getAttribute('data-conso-modo') || '',
    });

    // A guarda só precisa durar até aqui. `setTimeout` e não microtask: o
    // handler do cart-drawer abre a sacola num `.then` da promise do evento, e
    // microtask rodaria antes disso.
    setTimeout(liberar, 0);
  }

  /* ------------------------------------------------------------------ aviso */

  let aviso = null;

  /**
   * "Falta mais uma camisa": abre no lugar do brinde quando o modo alternativo
   * exige mais gatilhos do que a sacola tem. Reaproveita as classes da modal
   * de meta (sem a barra). O botão principal só fecha — o cliente fica na LP
   * pra escolher outra camisa; o link de escape abre a sacola.
   */
  function montarAviso(dados) {
    if (aviso) return aviso;

    aviso = document.createElement('dialog');
    aviso.className = 'conso-brinde-meta conso-brinde-meta--aviso';
    aviso.setAttribute('aria-labelledby', 'conso-brinde-aviso-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-brinde-meta__caixa';

    const fechar = document.createElement('button');
    fechar.type = 'button';
    fechar.className = 'conso-brinde-meta__x';
    fechar.setAttribute('aria-label', 'Fechar');
    fechar.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>';
    caixa.appendChild(fechar);

    const titulo = document.createElement('h2');
    titulo.className = 'conso-brinde-meta__titulo';
    titulo.id = 'conso-brinde-aviso-titulo';
    caixa.appendChild(titulo);

    const texto = document.createElement('p');
    texto.className = 'conso-brinde-meta__texto';
    texto.hidden = true;
    caixa.appendChild(texto);

    const acoes = document.createElement('div');
    acoes.className = 'conso-brinde-meta__acoes';

    const ficar = document.createElement('button');
    ficar.type = 'button';
    ficar.className = 'conso-brinde-meta__ir button';
    ficar.textContent = dados.modoBotao || 'Escolher outra camisa';

    const seguir = document.createElement('button');
    seguir.type = 'button';
    seguir.className = 'conso-brinde-meta__seguir';
    seguir.textContent = dados.modoFechar || 'Continuar para o carrinho';

    acoes.append(ficar, seguir);
    caixa.appendChild(acoes);
    aviso.appendChild(caixa);

    // Só o botão principal segura a sacola: "escolher outra camisa" é ficar na
    // página. X, fundo, ESC e o link de escape abrem a sacola.
    let ficando = false;
    ficar.addEventListener('click', () => {
      ficando = true;
      rastrear({ event: 'brinde_aviso_ficar' });
      aviso.close();
    });
    fechar.addEventListener('click', () => aviso.close());
    seguir.addEventListener('click', () => aviso.close());
    aviso.addEventListener('click', (e) => {
      if (e.target === aviso) aviso.close();
    });
    aviso.addEventListener('close', () => {
      if (!ficando) abrirSacola();
      else liberar();
      ficando = false;
    });

    aviso.__pintar = (faltam) => {
      titulo.textContent = String(dados.modoTitulo || 'Falta mais uma camisa para liberar a polo').replace(
        '{faltam}',
        String(faltam)
      );
      texto.textContent = dados.modoTexto || '';
      texto.hidden = !dados.modoTexto;
    };

    document.body.appendChild(aviso);
    return aviso;
  }

  function abrirAviso(dados, faltam) {
    const el = montarAviso(dados);
    el.__pintar(faltam);
    el.showModal();
    rastrear({ event: 'brinde_aviso_view', brinde_faltam: faltam });
    setTimeout(liberar, 0);
  }

  /* ---------------------------------------------------------- interceptação */

  document.addEventListener(
    'click',
    (evento) => {
      const alvo = evento.target instanceof Element ? evento.target.closest(SEL_ATC) : null;
      if (!alvo || alvo.disabled) return;

      const handle = handleDoBloco(alvo);
      const produtoId = produtoIdDoClique(alvo);
      const secaoId = secaoDoClique(alvo);
      if (!handle && !produtoId) return;
      if (!escolherDados(handle, produtoId, secaoId)) return;

      // A modal está aberta: o clique é o "adicionar brinde", que tem caminho
      // próprio e não pode reabrir a modal.
      if (modal && modal.open) return;

      pendente = true;
      handlePendente = handle;
      produtoIdPendente = produtoId;
      secaoPendente = secaoId;
      segurando = true;

      clearTimeout(expiracao);
      expiracao = setTimeout(desarmar, TETO_ESPERA_MS);
    },
    true
  );

  document.addEventListener(EVENTO_ADD, (evento) => {
    if (!pendente) return;

    // Só add do cliente. A ponte do `conso-cart-event-bridge` re-emite com
    // 'update', então nada que venha de script de fundo cai aqui.
    if (evento.action !== 'add') return;

    pendente = false;
    clearTimeout(expiracao);

    const dados = escolherDados(handlePendente, produtoIdPendente, secaoPendente);
    handlePendente = '';
    produtoIdPendente = '';
    secaoPendente = '';
    dadosDoFluxo = dados;

    if (!dados) {
      liberar();
      return;
    }

    const seguir = async () => {
      let carrinho = null;
      try {
        carrinho = await carrinhoAtual();
      } catch (e) {
        console.warn('[conso-lp-brinde] não deu pra ler o carrinho', e);
      }

      const minimo = minimoDeGatilhos(dados);

      if (dados.porUnidade) {
        // "1 brinde por unidade do gatilho, sem teto": compara quantas unidades
        // do gatilho já estão na sacola contra quantas do brinde o cliente já
        // pegou. Só oferece de novo se ainda sobrar cota.
        const unidades = unidadesDeGatilho(carrinho, dados);
        const cota = minimo > 0 ? Math.floor(unidades / minimo) : 0;
        if (quantidadeDoBrinde(carrinho, dados) >= cota) {
          abrirSacola();
          return;
        }
        abrirModal(dados);
        return;
      }

      // Já pegou ESTE brinde numa adição anterior: não repete a oferta.
      if (jaTemOBrinde(carrinho, dados)) {
        abrirSacola();
        return;
      }

      // Modo alternativo: ainda faltam gatilhos. Avisa em vez de oferecer.
      if (minimo > 1 && unidadesDeGatilho(carrinho, dados) < minimo) {
        abrirAviso(dados, minimo - unidadesDeGatilho(carrinho, dados));
        return;
      }

      abrirModal(dados);
    };

    const promessa = evento.promise;
    if (promessa && typeof promessa.then === 'function') {
      promessa
        .then((r) => {
          // O add falhou: devolve o drawer ao normal e sai.
          if (r && r.detail && r.detail.didError) {
            liberar();
            return;
          }
          return seguir();
        })
        .catch(() => liberar());
      return;
    }

    seguir();
  });

  document.addEventListener('cart:error', desarmar);
})();
